import { FormGroup, Input, Label } from "reactstrap";
import { handleFormChange } from "../../../components/modals/EditableTable";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export const ContainerGeneralInformation = ({
  formData,
  setFormData,
  setErrorForm,
}) => {
  const { t } = useTranslation();
  const [volumeIsDisabled, setVolumeIsDisabled] = useState(false);
  const [dimensionsIsDisabled, setDimensionsIsDisabled] = useState(false);

  // Initialiser l'état des champs au chargement du modal
  useEffect(() => {
    const hasVolume = formData.volume && formData.volume > 0;
    const hasDimensions =
      formData.dimensions?.length > 0 ||
      formData.dimensions?.width > 0 ||
      formData.dimensions?.height > 0;

    if (hasVolume) {
      // Si on a un volume, permettre de le modifier mais désactiver les dimensions
      setVolumeIsDisabled(false);
      setDimensionsIsDisabled(true);
    } else if (hasDimensions) {
      // Si on a des dimensions, permettre de les modifier mais désactiver le volume
      setVolumeIsDisabled(true);
      setDimensionsIsDisabled(false);
    } else {
      // Rien de renseigné, tout activer
      setVolumeIsDisabled(false);
      setDimensionsIsDisabled(false);
      setErrorForm(
        `Veuillez renseigner le volume <strong>OU</strong> les dimensions de votre contenant`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeVolumeField = (event) => {
    const newFormData = { ...formData };
    // ⚠️ IMPORTANT : Copier dimensions pour éviter "read only property" error
    newFormData.dimensions = formData.dimensions
      ? { ...formData.dimensions }
      : { length: 0, width: 0, height: 0 };

    if (event.target.value !== "") {
      // Effacer les dimensions quand on saisit un volume
      newFormData.dimensions.length = 0;
      newFormData.dimensions.width = 0;
      newFormData.dimensions.height = 0;
      newFormData.volume = parseFloat(event.target.value);

      setErrorForm("");
      setDimensionsIsDisabled(true);
      setVolumeIsDisabled(false);
    } else {
      // Si on efface le volume, tout redevient éditable
      newFormData.volume = 0;
      setDimensionsIsDisabled(false);
      setVolumeIsDisabled(false);
      setErrorForm(
        `Veuillez renseigner le volume <strong>OU</strong> les dimensions de votre contenant`,
      );
    }
    setFormData(newFormData);
  };

  const activeDimensionsField = (event) => {
    // handleFormChange a déjà mis à jour la dimension modifiée
    // On doit recalculer le volume après la mise à jour

    // Utiliser setTimeout pour s'assurer que handleFormChange a terminé
    setTimeout(() => {
      setFormData((currentFormData) => {
        const newFormData = { ...currentFormData };
        // ⚠️ IMPORTANT : Copier dimensions pour éviter "read only property" error
        newFormData.dimensions = currentFormData.dimensions
          ? { ...currentFormData.dimensions }
          : { length: 0, width: 0, height: 0 };

        // Récupérer les valeurs actuelles (après handleFormChange)
        const length = newFormData.dimensions.length || 0;
        const width = newFormData.dimensions.width || 0;
        const height = newFormData.dimensions.height || 0;

        // Calculer le volume
        const calculatedVolume = length * width * height;
        newFormData.volume = calculatedVolume;

        // Vérifier si toutes les dimensions sont vides
        const allEmpty = length === 0 && width === 0 && height === 0;

        if (allEmpty) {
          // Si tout est vide, réactiver le volume
          setVolumeIsDisabled(false);
          setDimensionsIsDisabled(false);
          setErrorForm(
            `Veuillez renseigner le volume <strong>OU</strong> les dimensions de votre contenant`,
          );
        } else {
          // Si au moins une dimension est renseignée, désactiver le volume
          setVolumeIsDisabled(true);
          setDimensionsIsDisabled(false);

          if (calculatedVolume === 0) {
            setErrorForm(
              `Veuillez renseigner le volume <strong>OU</strong> les dimensions de votre contenant`,
            );
          } else {
            setErrorForm("");
          }
        }

        return newFormData;
      });
    }, 0);
  };

  return (
    <section className="flex flex-col w-1/4 min-h-full">
      {/* Volume */}
      <FormGroup className="flex flex-col w-full mb-3">
        <Label>
          Volume (m<sup>3</sup>)
        </Label>
        <Input
          className={`${volumeIsDisabled ? "bg-gainsboro-transparent" : "bg-white"} mx-1  border border-gray-300 text-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 `}
          type="number"
          placeholder={`${t("Numbers only")} : 30`}
          id="volume"
          value={formData.volume > 0 ? formData.volume : ""}
          onChange={(event) => {
            activeVolumeField(event);
          }}
          name="volume"
          step={0.01}
          disabled={volumeIsDisabled}
        />
      </FormGroup>

      {/* Longeur */}
      <FormGroup className="flex flex-col w-full mb-3">
        <Label>{t("Length")} (m)</Label>
        <Input
          className={`${dimensionsIsDisabled ? "bg-gainsboro-transparent" : "bg-white"} mx-1 border border-gray-300 text-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 `}
          type="number"
          id="length"
          placeholder={`${t("Numbers only")} : 5.8`}
          value={
            formData.dimensions?.length > 0 ? formData.dimensions.length : ""
          }
          name="length"
          dictcategory="dimensions"
          onChange={(event) => {
            handleFormChange(event, formData, setFormData);
            activeDimensionsField(event);
          }}
          step={0.01}
          disabled={dimensionsIsDisabled}
        />
      </FormGroup>

      {/* Largeur */}
      <FormGroup className="flex flex-col w-full mb-3">
        <Label>{t("Width")} (m)</Label>
        <Input
          className={`${dimensionsIsDisabled ? "bg-gainsboro-transparent" : "bg-white"} mx-1 border border-gray-300 text-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 `}
          type="number"
          id="width"
          placeholder={`${t("Numbers only")} : 5.8`}
          value={
            formData.dimensions?.width > 0 ? formData.dimensions.width : ""
          }
          name="width"
          dictcategory="dimensions"
          onChange={(event) => {
            handleFormChange(event, formData, setFormData);
            activeDimensionsField(event);
          }}
          step={0.01}
          disabled={dimensionsIsDisabled}
        />
      </FormGroup>

      {/* Hauteur */}
      <FormGroup className="flex flex-col w-full mb-3">
        <Label>{t("Height")} (m)</Label>
        <Input
          className={`${dimensionsIsDisabled ? "bg-gainsboro-transparent" : "bg-white"} mx-1 border border-gray-300 text-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 `}
          type="number"
          placeholder={`${t("Numbers only")} : 5.8`}
          value={
            formData.dimensions?.height > 0 ? formData.dimensions.height : ""
          }
          name="height"
          dictcategory="dimensions"
          onChange={(event) => {
            handleFormChange(event, formData, setFormData);
            activeDimensionsField(event);
          }}
          step={0.01}
          disabled={dimensionsIsDisabled}
        />
      </FormGroup>
    </section>
  );
};
